import { INITIAL_GALLERIES, FEATURED_PROJECTS, SITE_INFO, BRAND_ASSETS } from '../data';

const CMS_BASE = "https://api.dev.auxo.dk/public";

// Hjælpe-mappings for pæne labels fra taksonomi-slugs
const CATEGORY_LABELS = {
  'betonarbejde': 'Betonarbejde',
  'entreprenoer-og-anlaeg': 'Entreprenør og anlæg',
  'kloak-og-klimasikring': 'Kloak og klimasikring',
  'ledelse-og-administration': 'Ledelse og administration',
  'kloak-og-tv-inspektion': 'Kloak og TV-inspektion',
  'vaerksted': 'Værksted'
};

/**
 * Hent et specifikt galleri ud fra placering/nøgle (f.eks. 'entreprenerservice', 'betonarbejde')
 */
export async function getGalleryByKey(galleryKey) {
  try {
    // Søg via taksonomi-term eller hent fra gallerier
    const res = await fetch(`${CMS_BASE}/v2/content/gallerier?term=galleri-placering:${encodeURIComponent(galleryKey)}&limit=1`);
    if (res.ok) {
      const data = await res.json();
      if (data && data.items && data.items.length > 0) {
        const item = data.items[0];
        return {
          title: item.titel || item.title,
          description: item.beskrivelse || item.description || '',
          images: item.billeder || item.images || [],
          gallery_key: galleryKey
        };
      }
    }
  } catch (err) {
    console.warn("CMS galleri fetch fejl, benytter fallback:", err);
  }

  // Fallback
  return INITIAL_GALLERIES.find((g) => g.gallery_key === galleryKey) || null;
}

/**
 * Hent alle gallerier
 */
export async function getAllGalleries() {
  try {
    const res = await fetch(`${CMS_BASE}/v2/content/gallerier?limit=50`);
    if (res.ok) {
      const data = await res.json();
      if (data && data.items && data.items.length > 0) {
        return data.items.map(item => ({
          title: item.titel,
          description: item.beskrivelse || '',
          images: item.billeder || [],
          gallery_key: item.terms?.['galleri-placering']?.[0] || ''
        }));
      }
    }
  } catch (err) {
    console.warn("CMS gallerier fetch fejl, benytter fallback:", err);
  }
  return INITIAL_GALLERIES;
}

/**
 * Hent sideindhold fra CMS ud fra side_slug
 */
export async function getPageBySlug(pageSlug) {
  try {
    const cleanSlug = pageSlug.replace(/^\//, '') || 'index';
    const res = await fetch(`${CMS_BASE}/v2/content/sider?side_slug=${encodeURIComponent(cleanSlug)}&limit=1`);
    if (res.ok) {
      const data = await res.json();
      if (data && data.items && data.items.length > 0) {
        const item = data.items[0];
        return {
          title: item.titel,
          page_slug: item.side_slug,
          meta_description: item.meta_beskrivelse || '',
          og_title: item.og_titel || item.titel,
          og_description: item.og_beskrivelse || '',
          og_image: Array.isArray(item.og_billede) && item.og_billede.length > 0 ? item.og_billede[0] : null,
          content_html: item.indhold_html || '',
          gallery_key: item.terms?.['galleri-placering']?.[0] || ''
        };
      }
    }
  } catch (err) {
    console.warn("CMS sider fetch fejl:", err);
  }
  return null;
}

/**
 * Hent menupunkter fra CMS
 */
export async function getMenuItems() {
  try {
    const res = await fetch(`${CMS_BASE}/v2/content/menu_punkter?sort=sortering&order=asc&limit=50`);
    if (res.ok) {
      const data = await res.json();
      if (data && data.items && data.items.length > 0) {
        return data.items.map(item => ({
          label: item.titel,
          href: item.link,
          order: item.sortering,
          parent: item.terms?.['menu-foraelder']?.[0] || ''
        }));
      }
    }
  } catch (err) {
    console.warn("CMS menu_punkter fetch fejl:", err);
  }
  return [];
}

/**
 * Hent generelle sideindstillinger (logo, hero_billede, om_os_billede, lastbil_billede)
 */
export async function getSiteSettings() {
  try {
    const res = await fetch(`${CMS_BASE}/v2/content/side_indstillinger?limit=50`);
    if (res.ok) {
      const data = await res.json();
      if (data && data.items && data.items.length > 0) {
        const settingsMap = {};
        data.items.forEach(item => {
          const keyTerm = item.terms?.['indstillings-type']?.[0];
          const imgUrl = Array.isArray(item.billede) && item.billede.length > 0 ? item.billede[0] : null;
          const entry = {
            title: item.titel,
            image: imgUrl,
            value: item.vaerdi,
            description: item.beskrivelse
          };

          if (keyTerm) {
            settingsMap[keyTerm] = entry;
            // Opret også underscore / alias varianter
            const underscoreKey = keyTerm.replace(/-/g, '_');
            settingsMap[underscoreKey] = entry;
          }
        });
        return settingsMap;
      }
    }
  } catch (err) {
    console.warn("CMS side_indstillinger fetch fejl:", err);
  }
  return {};
}

/**
 * Hent udvalgte projekter til forside og projektoversigt
 */
export async function getFeaturedProjects() {
  try {
    const res = await fetch(`${CMS_BASE}/v2/content/udvalgte_projekter?sort=sortering&order=asc&limit=20`);
    if (res.ok) {
      const data = await res.json();
      if (data && data.items && data.items.length > 0) {
        return data.items.map(item => {
          const catTerm = item.terms?.['projekt-kategorier']?.[0] || '';
          return {
            id: item.id,
            title: item.titel,
            category: CATEGORY_LABELS[catTerm] || catTerm || '',
            description: item.beskrivelse || '',
            href: item.link || '/projekter',
            image: Array.isArray(item.billede) && item.billede.length > 0 ? item.billede[0] : BRAND_ASSETS.defaultOgImage
          };
        });
      }
    }
  } catch (err) {
    console.warn("CMS udvalgte_projekter fetch fejl:", err);
  }
  return FEATURED_PROJECTS;
}

/**
 * Hent ydelser / fagområder til forsiden
 */
export async function getServiceAreas() {
  const defaultServices = [
    {
      title: 'Entreprenør og anlæg',
      desc: 'Jordarbejde, byggemodning, belægning, dræn, haveanlæg og gårdrenovering.',
      href: '/entreprenerservice',
      icon: 'fa-trowel-bricks',
      image: 'https://assets.auxodev.dk/assets/e25852aa-8434-4aa0-ac76-be91340b9170/dumper-1.jpg'
    },
    {
      title: 'Kloak og klimasikring',
      desc: 'Autoriseret kloakarbejde, højvandslukker, rottespærre, faskiner, omfangsdræn og løsninger mod skybrud.',
      href: '/kloak-service',
      icon: 'fa-faucet-drip',
      image: 'https://assets.auxodev.dk/assets/e25852aa-8434-4aa0-ac76-be91340b9170/g4480-807b-7ad5-0b72-65be26b01624.jpg'
    },
    {
      title: 'Betonarbejde',
      desc: 'Fundamenter, terrændæk, støttemure, kældre, forskalling og armering.',
      href: '/betonarbejde',
      icon: 'fa-cubes-stacked',
      image: 'https://assets.auxodev.dk/assets/e25852aa-8434-4aa0-ac76-be91340b9170/beton-ny-grund.jpg'
    },
    {
      title: 'TV-inspektion og spuling',
      desc: 'Undersøgelse af kloakledninger, lokalisering af skader, kloakspuling og dokumentation.',
      href: '/tv-inspektion',
      icon: 'fa-video',
      image: 'https://assets.auxodev.dk/assets/e25852aa-8434-4aa0-ac76-be91340b9170/3259.jpg'
    },
    {
      title: 'Materialer og genbrug',
      desc: 'På vores plads i Frederiksværk sælger vi grus, sand, muld og genbrugsmaterialer til private og erhverv.',
      href: '/bygningsaffald',
      icon: 'fa-recycle',
      image: 'https://assets.auxodev.dk/assets/e25852aa-8434-4aa0-ac76-be91340b9170/beton-brokker.jpg'
    },
    {
      title: 'Udlejning',
      desc: 'Udlejning af køreplader, gangbroer, byggepladshegn og andet materiel.',
      href: '/udlejning',
      icon: 'fa-truck-ramp-box',
      image: 'https://assets.auxodev.dk/assets/e25852aa-8434-4aa0-ac76-be91340b9170/gangbro.jpg'
    }
  ];

  try {
    const res = await fetch(`${CMS_BASE}/v2/content/ydelser?sort=sortering&order=asc&limit=20`);
    if (res.ok) {
      const data = await res.json();
      if (data && data.items && data.items.length > 0) {
        return data.items.map(item => ({
          title: item.titel,
          desc: item.beskrivelse || '',
          href: item.link || '/bygningsaffald',
          icon: item.terms?.['ydelse-ikon']?.[0] || 'fa-tools',
          image: Array.isArray(item.billede) && item.billede.length > 0 ? item.billede[0] : BRAND_ASSETS.defaultOgImage
        }));
      }
    }
  } catch (err) {
    console.warn("CMS ydelser fetch fejl:", err);
  }
  return defaultServices;
}

/**
 * Hent medarbejdere til om os og kontaktsiden
 */
export async function getTeamMembers() {
  try {
    const res = await fetch(`${CMS_BASE}/v2/content/medarbejdere?sort=sortering&order=asc&limit=20`);
    if (res.ok) {
      const data = await res.json();
      if (data && data.items && data.items.length > 0) {
        return data.items.map(item => {
          const depTerm = item.terms?.['afdelinger']?.[0] || '';
          return {
            name: item.navn,
            role: item.stilling,
            category: CATEGORY_LABELS[depTerm] || depTerm || '',
            phone: item.telefon || '',
            email: item.email || '',
            image: Array.isArray(item.billede) && item.billede.length > 0 ? item.billede[0] : null
          };
        });
      }
    }
  } catch (err) {
    console.warn("CMS medarbejdere fetch fejl:", err);
  }
  return SITE_INFO.contacts;
}
